import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  FileCheck,
  Percent,
  Clock,
  Activity,
  Monitor,
  Coins,
  Timer,
  MessageSquare,
  Wrench,
  MessagesSquare,
} from "lucide-react";
import type {
  TBrokerAnalytics,
  TClaimStatusAnalytics,
  TSessionAnalytics,
  TConversationAnalytics,
  TToolCallAnalytics,
  TChatMessageAnalytics,
} from "@/types/TAnalytics";

type KPICardProps = {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  icon: React.ReactNode;
};

function KPICard({ title, value, subtitle, trend, icon }: KPICardProps) {
  const getTrendIcon = () => {
    if (trend === undefined || trend === 0) return <Minus className="h-4 w-4 text-gray-400" />;
    if (trend > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const getTrendColor = () => {
    if (trend === undefined || trend === 0) return "text-gray-400";
    if (trend > 0) return "text-green-500";
    return "text-red-500";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate pr-2">{title}</span>
        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-shrink-0">
          {icon}
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div className="min-w-0">
          <p className="text-2xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>}
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 flex-shrink-0 ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="text-sm font-medium">
              {trend > 0 ? "+" : ""}{trend.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

type KPICardsProps = {
  broker: TBrokerAnalytics;
  claimStatus: TClaimStatusAnalytics;
  sessionData?: TSessionAnalytics | null;
  conversations?: TConversationAnalytics | null;
  toolCalls?: TToolCallAnalytics | null;
  chatMessages?: TChatMessageAnalytics | null;
};

export function KPICards({ broker, claimStatus, sessionData, conversations, toolCalls, chatMessages }: KPICardsProps) {
  const { t } = useTranslation();

  const totalQueries = broker.total + claimStatus.total;
  const totalSuccess = broker.success + claimStatus.success;
  const overallSuccessRate = totalQueries > 0 ? (totalSuccess / totalQueries) * 100 : 0;

  const avgResponseTime = broker.total + claimStatus.total > 0
    ? Math.round(
        (broker.avg_response_time_ms * broker.total + claimStatus.avg_response_time_ms * claimStatus.total) /
        (broker.total + claimStatus.total)
      )
    : 0;

  const currentWeekTotal = broker.trend.current_week + claimStatus.trend.current_week;
  const previousWeekTotal = broker.trend.previous_week + claimStatus.trend.previous_week;
  const overallTrend = previousWeekTotal > 0
    ? ((currentWeekTotal - previousWeekTotal) / previousWeekTotal) * 100
    : 0;

  const intentCount = (intent: string) =>
    chatMessages?.by_intent.find(b => b.intent === intent)?.count ?? 0;
  const brokerIntent = intentCount("broker");
  const claimStatusIntent = intentCount("claim_status");
  const claimInfoIntent = intentCount("claim_info");
  const generalIntent = intentCount("general");

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      <KPICard
        title={t("analytics.kpi.conversations", "Konversationen")}
        value={conversations?.total ?? 0}
        subtitle={t("analytics.kpi.conversationsSubtitle", "Realtime-API Sitzungen")}
        trend={conversations?.trend?.change_percent}
        icon={<MessageSquare className="h-5 w-5 text-indigo-500" />}
      />
      <KPICard
        title={t("analytics.kpi.toolCalls", "Tool-Aufrufe")}
        value={toolCalls?.total ?? 0}
        subtitle={`${toolCalls?.success ?? 0} ${t("analytics.kpi.successful", "erfolgreich")} / ${toolCalls?.failed ?? 0} ${t("analytics.kpi.toolFailed", "fehlgeschlagen")}`}
        icon={<Wrench className="h-5 w-5 text-orange-500" />}
      />
      <KPICard
        title={t("analytics.kpi.totalQueries", "Gesamt-Abfragen")}
        value={totalQueries}
        subtitle={`${broker.total} ${t("analytics.trend.broker", "Berater")} + ${claimStatus.total} ${t("analytics.trend.claimStatus", "Status")}`}
        trend={overallTrend}
        icon={<Activity className="h-5 w-5 text-blue-500" />}
      />
      <KPICard
        title={t("analytics.kpi.successRate", "Erfolgsrate")}
        value={`${overallSuccessRate.toFixed(1)}%`}
        subtitle={`${totalSuccess} / ${totalQueries} ${t("analytics.kpi.queries", "Abfragen")}`}
        icon={<Percent className="h-5 w-5 text-blue-500" />}
      />
      <KPICard
        title={t("analytics.kpi.avgResponseTime", "Ø Antwortzeit")}
        value={`${avgResponseTime}ms`}
        subtitle={t("analytics.kpi.responseTimeSubtitle", "Durchschnitt")}
        icon={<Clock className="h-5 w-5 text-blue-500" />}
      />

      <KPICard
        title={t("analytics.kpi.brokerQueries", "Berater-Abfragen")}
        value={broker.total}
        subtitle={`${broker.blacklisted} ${t("analytics.kpi.blacklisted", "Sonderfälle")} · ${brokerIntent} ${t("analytics.kpi.chatIntents", "Chat-Intents")}`}
        trend={broker.trend.change_percent}
        icon={<Users className="h-5 w-5 text-blue-500" />}
      />
      <KPICard
        title={t("analytics.kpi.claimQueries", "Schadenstatus-Abfragen")}
        value={claimStatus.total}
        subtitle={`${claimStatus.success} ${t("analytics.kpi.successful", "erfolgreich")} · ${claimStatusIntent} ${t("analytics.kpi.chatIntents", "Chat-Intents")}`}
        trend={claimStatus.trend.change_percent}
        icon={<FileCheck className="h-5 w-5 text-blue-500" />}
      />
      <KPICard
        title={t("analytics.kpi.chatMessages", "Chat-Nachrichten")}
        value={chatMessages?.total ?? 0}
        subtitle={`${chatMessages?.unique_sessions ?? 0} ${t("analytics.kpi.chatSessions", "Sessions")} · ${generalIntent} ${t("analytics.kpi.chatGeneral", "allg.")} / ${claimInfoIntent} ${t("analytics.kpi.chatClaimInfo", "Schadenmeldung")}`}
        trend={chatMessages?.trend?.change_percent}
        icon={<MessagesSquare className="h-5 w-5 text-emerald-500" />}
      />
      <KPICard
        title={t("analytics.kpi.totalSessions", "Sessions")}
        value={sessionData?.total ?? 0}
        subtitle={`${sessionData?.active ?? 0} ${t("analytics.kpi.activeSessions", "aktiv")} / ${sessionData?.historic ?? 0} ${t("analytics.kpi.historicSessions", "beendet")}`}
        icon={<Monitor className="h-5 w-5 text-purple-500" />}
      />
      <KPICard
        title={t("analytics.kpi.totalCredits", "Credits verbraucht")}
        value={sessionData?.total_credits?.toFixed(2) ?? "0"}
        subtitle={t("analytics.kpi.creditsSubtitle", "LiveAvatar Credits")}
        icon={<Coins className="h-5 w-5 text-amber-500" />}
      />
      <KPICard
        title={t("analytics.kpi.totalDuration", "Gesamt-Dauer")}
        value={formatDuration(sessionData?.total_duration ?? 0)}
        subtitle={`Ø ${formatDuration(sessionData?.avg_duration ?? 0)} ${t("analytics.kpi.perSession", "pro Session")}`}
        icon={<Timer className="h-5 w-5 text-teal-500" />}
      />
    </div>
  );
}
